// tippi/src/pages/admin/support/SupportCases.js
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, limit, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { supportApi } from '../../../firebase/functionsService';
import { COLORS } from '../../../styles/tokens';
import { useIsMobile } from '../../../hooks/useIsMobile';

export const STATUS_LABEL = { new: 'Ny', in_progress: 'Under arbeid', waiting: 'Venter på kunde', resolved: 'Løst' };
export const CHANNEL_LABEL = { app: 'App', phone: 'Telefon', email: 'E-post' };

export default function SupportCases() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [status, setStatus] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const constraints = [];
      if (status) constraints.push(where('status', '==', status));
      constraints.push(orderBy('lastMessageAt', 'desc'), limit(100));
      const snap = await getDocs(query(collection(db, 'supportCases'), ...constraints));
      setRows(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      setError('Kunne ikke laste sakene. Mangler en indeks, står lenke til å opprette den i nettleserkonsollen.');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ padding: isMobile ? 16 : 24, flex: 1, overflowY: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, flex: 1 }}>Saker</h2>
        <label style={labelStyle}>Status
          <select aria-label="Status" value={status} onChange={e => setStatus(e.target.value)} style={inputStyle}>
            <option value="">Alle</option>
            {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <button type="button" onClick={() => setFormOpen(o => !o)} style={primaryBtn}>Ny telefonsak</button>
      </div>

      {formOpen && <PhoneCaseForm onCreated={(id) => navigate(`/admin/support/${id}`)} />}

      {error && <p role="alert" style={{ color: COLORS.dangerText, fontSize: 13 }}>{error}</p>}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
              {['Sist', 'Kanal', 'Fra', 'Sak', 'Status'].map(h => (
                <th key={h} style={{ padding: '8px 10px', color: COLORS.textLight, fontWeight: 600, textTransform: 'uppercase', fontSize: 11, textAlign: 'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} onClick={() => navigate(`/admin/support/${r.id}`)} style={{ borderBottom: `1px solid ${COLORS.border}`, cursor: 'pointer' }}>
                <td style={{ padding: 10, color: COLORS.textLight, fontSize: 12, whiteSpace: 'nowrap' }}>{r.lastMessageAt?.toDate?.().toLocaleString('nb-NO') ?? '—'}</td>
                <td style={{ padding: 10 }}>{CHANNEL_LABEL[r.channel] ?? r.channel}</td>
                <td style={{ padding: 10 }}>{r.userName ?? r.contactName ?? '—'}</td>
                <td style={{ padding: 10, fontWeight: r.unreadForAdmin ? 700 : 400 }}>{r.title}</td>
                <td style={{ padding: 10 }}>
                  <span style={{ background: COLORS.surfaceHover, padding: '2px 8px', borderRadius: 4, fontSize: 12 }}>{STATUS_LABEL[r.status] ?? r.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {loading && <p style={{ color: COLORS.textLight, padding: '12px 0', fontSize: 13 }}>Laster…</p>}
      {!loading && !error && rows.length === 0 && <p style={{ color: COLORS.textLight, padding: '24px 0', fontSize: 13 }}>Ingen saker</p>}
    </div>
  );
}

function PhoneCaseForm({ onCreated }) {
  const [f, setF] = useState({ name: '', phone: '', userEmail: '', title: '', text: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setF(prev => ({ ...prev, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError('');
    try {
      const res = await supportApi.adminCreatePhoneCase({
        name: f.name.trim(), phone: f.phone.trim(), userEmail: f.userEmail.trim(), title: f.title.trim(), text: f.text.trim(),
      });
      onCreated(res.caseId);
    } catch (err) {
      setError(err?.message ?? 'Kunne ikke opprette saken.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 14, marginBottom: 20, display: 'grid', gap: 10, maxWidth: 560 }}>
      <label style={labelStyle}>Navn<input aria-label="Navn" value={f.name} onChange={set('name')} style={inputStyle} /></label>
      <label style={labelStyle}>Telefon<input aria-label="Telefon" value={f.phone} onChange={set('phone')} style={inputStyle} /></label>
      <label style={labelStyle}>Brukerens e-post (valgfri)<input aria-label="Brukerens e-post (valgfri)" value={f.userEmail} onChange={set('userEmail')} style={inputStyle} placeholder="Kobler saken til brukeren hvis den finnes" /></label>
      <label style={labelStyle}>Tittel<input aria-label="Tittel" value={f.title} onChange={set('title')} style={inputStyle} maxLength={120} /></label>
      <label style={labelStyle}>Henvendelsen<textarea aria-label="Henvendelsen" value={f.text} onChange={set('text')} rows={4} style={inputStyle} maxLength={2000} /></label>
      {error && <p role="alert" style={{ color: COLORS.dangerText, fontSize: 13, margin: 0 }}>{error}</p>}
      <button type="submit" disabled={busy || !f.title.trim() || !f.text.trim()} style={{ ...primaryBtn, opacity: busy || !f.title.trim() || !f.text.trim() ? 0.5 : 1 }}>Opprett sak</button>
    </form>
  );
}

const labelStyle = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: COLORS.textLight, fontWeight: 600 };
const inputStyle = { padding: '6px 10px', borderRadius: 6, border: `1px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textDark, fontSize: 13, fontFamily: 'inherit' };
const primaryBtn = { padding: '7px 16px', border: 'none', borderRadius: 6, background: COLORS.primary, color: COLORS.onPrimary, fontWeight: 700, fontSize: 13, cursor: 'pointer' };
